"use client";

import { CtaComponentProps } from "@/cms/models";
import {
  getBorderRadius,
  getDesktop,
  getMargins,
  getMobile,
  getPaddings,
  getTextSize,
} from "@/utils/theme-helper";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useCallback, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useLookSystem } from "@/components/motion/LookMotion";
import { rippleAllowed } from "@/utils/motion";

type Ripple = {
  id: number;
  x: number;
  y: number;
  size: number;
};

const DEFAULTS: Partial<CtaComponentProps> = {
  link: "#",
  label: "",

  rounded: undefined,
  margin: "0",
  justify: "center",
  textColor: "var(--button-text-color)",
  hoverTextColor: "var(--button-hover-text-color)",
  activeTextColor: "var(--button-active-text-color)",
  bgColor: "var(--button-bg-color)",
  hoverBgColor: "var(--button-hover-bg-color)",
  activeBgColor: "var(--button-active-bg-color)",
  borderColor: "var(--button-border-color)",
  hoverBorderColor: "var(--button-hover-border-color)",
  activeBorderColor: "var(--button-active-border-color)",
  size: "sm",
  padding: "sm",
  width: "auto",
  height: "auto",
  disableAnimation: false,
};

/** A press colour the caller leaves out is the primary button's, unless the caller colours the button itself: then the
 *  button presses into its hover colour, as a look-system pill does (look-buttons.css). */
function pressColours(
  own: Partial<CtaComponentProps>,
  config: Partial<CtaComponentProps>,
) {
  const ownColours = (["bgColor", "hoverBgColor"] as const).some(
    (key) => own[key] !== undefined && own[key] !== DEFAULTS[key],
  );
  const press = (
    active: "activeBorderColor" | "activeBgColor" | "activeTextColor",
    hover: "hoverBorderColor" | "hoverBgColor" | "hoverTextColor",
  ) =>
    ownColours && own[active] === undefined ? config[hover] : config[active];
  return {
    borderColor: press("activeBorderColor", "hoverBorderColor"),
    backgroundColor: press("activeBgColor", "hoverBgColor"),
    color: press("activeTextColor", "hoverTextColor"),
  };
}

export default function RippleCtaButton(props: {
  children?: React.ReactNode;
  className?: string;
  suppressRoundedClass?: boolean;
  config?: Partial<CtaComponentProps>;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  type?: "button" | "submit" | "reset" | undefined;
  isActive?: boolean;
  "aria-label"?: string;
  "aria-haspopup"?: "dialog";
}) {
  let { config } = props;

  config = { ...DEFAULTS, ...(config ?? {}) };

  const [ripples, setRipples] = useState<Ripple[]>([]);
  const reducedMotion = useReducedMotion();
  const lookSystem = useLookSystem() || !!config.lookSystem;
  const idRef = useRef(0);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const createRipple = useCallback(
    (originX: number, originY: number) => {
      const button = buttonRef.current;
      if (!button || (lookSystem && !rippleAllowed(button))) return;

      const rect = button.getBoundingClientRect();
      const localX = originX - rect.left;
      const localY = originY - rect.top;
      const dx = Math.max(localX, rect.width - localX);
      const dy = Math.max(localY, rect.height - localY);
      const radius = Math.sqrt(dx * dx + dy * dy);
      const size = radius * 2;

      const id = ++idRef.current;
      setRipples((prev) => [...prev, { id, x: localX, y: localY, size }]);

      return id;
    },
    [lookSystem],
  );

  const removeLastRipple = useCallback(() => {
    setRipples((prev) => (prev.length ? prev.slice(0, prev.length - 1) : prev));
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      // motion's keyboard press (whileTap) dispatches a pointerdown without a pointerType at the viewport's origin;
      // onKeyDown already draws the key's ripple.
      if (event.isPrimary && event.pointerType) {
        createRipple(event.clientX, event.clientY);
      }
    },
    [createRipple],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.repeat) return;
      if (event.key === " " || event.key === "Enter") {
        const button = buttonRef.current;
        if (!button) return;
        const rect = button.getBoundingClientRect();
        createRipple(rect.left + rect.width / 2, rect.top + rect.height / 2);
      }
    },
    [createRipple],
  );

  const onKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === " " || event.key === "Enter") {
        removeLastRipple();
      }
    },
    [removeLastRipple],
  );

  const mobileWidth = getMobile("none", config.width);
  const desktopWidth = getDesktop("none", config.width);
  const mobileHeight = getMobile("none", config.height);
  const desktopHeight = getDesktop("none", config.height);

  return (
    <div
      data-type="CtaComponent"
      className={twMerge(
        `flex ${getTextSize(config?.size, "sm")} ${getMargins(config.margin)} justify-${config.justify} ${mobileWidth !== "none" ? "w-[var(--mobile-width)]" : ""}`,
        `${desktopWidth !== "none" ? "md:w-[var(--desktop-width)]" : ""} ${mobileHeight !== "none" ? "h-[var(--mobile-height)]" : ""} ${desktopHeight !== "none" ? "md:h-[var(--desktop-height)]" : ""}`,
      )}
      style={
        {
          "--mobile-width": mobileWidth,
          "--desktop-width": desktopWidth,
          "--mobile-height": mobileHeight,
          "--desktop-height": desktopHeight,
        } as React.CSSProperties
      }
    >
      <motion.button
        ref={buttonRef}
        data-re-press={lookSystem ? "" : undefined}
        disabled={props.disabled}
        type={props.type || "button"}
        aria-label={props["aria-label"]}
        aria-haspopup={props["aria-haspopup"]}
        onClick={(e) => props.onClick?.(e)}
        style={
          {
            // Motion owns the colour interpolation; CSS must not ease each frame again.
            transitionProperty: "none",
            "--rounding-button-mobile": getBorderRadius(
              getMobile("", config.rounded),
              "button",
              config.lookSystem,
            ),
            "--rounding-button-desktop": getBorderRadius(
              getDesktop("", config.rounded),
              "button",
              config.lookSystem,
            ),
          } as React.CSSProperties
        }
        className={twMerge(
          `relative ${props.suppressRoundedClass ? "" : "homepage-button-rounded"} border ${config.padding ? getPaddings(config.padding) : "px-3 py-2"} flex justify-center lg:min-w-[122px] disabled:opacity-20`,
          props.className,
        )}
        onPointerDown={onPointerDown}
        onPointerUp={removeLastRipple}
        onPointerCancel={removeLastRipple}
        onPointerLeave={removeLastRipple}
        onBlur={removeLastRipple}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        animate={props.isActive ? "active" : undefined}
        whileHover={{
          borderColor: config.hoverBorderColor,
          backgroundColor: config.hoverBgColor,
          color: config.hoverTextColor,
        }}
        whileTap={
          lookSystem ? pressColours(props.config ?? {}, config) : undefined
        }
        initial={{
          borderColor: config.borderColor,
          backgroundColor: config.bgColor,
          color: config.textColor,
        }}
        variants={{
          active: {
            borderColor: config.activeBorderColor,
            backgroundColor: config.activeBgColor,
            color: config.activeTextColor,
          },
        }}
        transition={{
          duration: reducedMotion ? 0 : 0.15,
          ease: "linear",
        }}
      >
        {props.children}
        <span className="ripple-container" aria-hidden>
          <AnimatePresence>
            {ripples.map((ripple) => (
              <motion.span
                key={ripple.id}
                className="ripple"
                style={{
                  width: ripple.size,
                  height: ripple.size,
                  left: ripple.x - ripple.size / 2,
                  top: ripple.y - ripple.size / 2,
                }}
                initial={{ opacity: 0, transform: "scale(0)" }}
                animate={{
                  opacity: 0.4,
                  transform: "scale(1)",
                  transition: { duration: 0.3 },
                }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.55,
                  ease: "easeOut",
                }}
              />
            ))}
          </AnimatePresence>
        </span>
      </motion.button>
      <Stylesheet />
    </div>
  );
}

function Stylesheet() {
  return (
    <style>{`
      .ripple-container {
        position: absolute;
        inset: 0;
        overflow: hidden;
        border-radius: inherit;
        pointer-events: none;
      }

      .ripple {
        position: absolute;
        border-radius: 50%;
        background-color: currentColor;
        will-change: opacity, transform;
      }
    `}</style>
  );
}
